// ============================================================
// DockerCompose.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: From "Manual" to "Automated". 1 File = 1 Environment.
// - Key Term: "YAML" (Yet Another Markup Language).
// - Strategy: Visualize the multi-container stack.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LayersIcon from "@mui/icons-material/Layers";
import TerminalIcon from "@mui/icons-material/Terminal";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TERMINAL_BG = "#011627";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const YamlWindow = styled(Paper)(({ theme }) => ({
    backgroundColor: TERMINAL_BG,
    padding: theme.spacing(4),
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    fontFamily: "'Fira Code', monospace",
    boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
    position: 'relative',
    overflow: 'hidden',
    "&::before": {
        content: '"docker-compose.yml"',
        position: 'absolute',
        top: 0,
        right: 0,
        padding: '4px 12px',
        fontSize: '0.7rem',
        backgroundColor: BRAND.border,
        color: DOCKER_BLUE,
        borderRadius: '0 0 0 12px'
    }
}));

const ServiceNode = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    mb: theme.spacing(2)
}));

export default function DockerCompose() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Orchestration · Module 09"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Docker <span style={{ color: DOCKER_BLUE }}>Compose</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        Why run five commands when you can run one? Docker Compose allows you to
                        define and share multi-container applications as a single YAML file.
                    </Typography>
                </Box>

                {/* 2. YAML VS SERVICES VISUAL */}
                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7}>
                        <YamlWindow>
                            <Typography sx={{ color: "#C792EA", mb: 0.5 }}>version: <span style={{ color: "#ADDB67" }}>'3.8'</span></Typography>
                            <Typography sx={{ color: "#C792EA", mb: 0.5 }}>services:</Typography>
                            <Typography sx={{ color: "#82AAFF", ml: 2, mb: 0.5 }}>web-app:</Typography>
                            <Typography sx={{ color: "#82AAFF", ml: 4, mb: 0.5 }}>build: .</Typography>
                            <Typography sx={{ color: "#82AAFF", ml: 4, mb: 0.5 }}>ports:</Typography>
                            <Typography sx={{ color: "#ADDB67", ml: 6, mb: 0.5 }}>- "3000:3000"</Typography>
                            <Typography sx={{ color: "#82AAFF", ml: 2, mt: 1, mb: 0.5 }}>database:</Typography>
                            <Typography sx={{ color: "#ADDB67", ml: 4, mb: 0.5 }}>image: postgres:latest</Typography>
                            <Typography sx={{ color: "#82AAFF", ml: 4, mb: 0.5 }}>volumes:</Typography>
                            <Typography sx={{ color: "#ADDB67", ml: 6 }}>- db-data:/var/lib/postgresql/data</Typography>
                        </YamlWindow>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Box sx={{ p: 4, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 4, height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 4, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountTreeIcon sx={{ color: DOCKER_BLUE }} /> Service Stack
                            </Typography>

                            <ServiceNode>
                                <Chip label="UI" size="small" sx={{ bgcolor: DOCKER_BLUE, color: NAVY_BG, fontWeight: 900 }} />
                                <Typography variant="body2">Frontend (React)</Typography>
                            </ServiceNode>

                            <Box sx={{ textAlign: 'center', my: 1, opacity: 0.5 }}><LayersIcon fontSize="small" /></Box>

                            <ServiceNode>
                                <Chip label="API" size="small" sx={{ bgcolor: DOCKER_BLUE, color: NAVY_BG, fontWeight: 900 }} />
                                <Typography variant="body2">Backend (Node/Python)</Typography>
                            </ServiceNode>

                            <Box sx={{ textAlign: 'center', my: 1, opacity: 0.5 }}><LayersIcon fontSize="small" /></Box>

                            <ServiceNode>
                                <Chip label="DB" size="small" sx={{ bgcolor: "#00ED64", color: NAVY_BG, fontWeight: 900 }} />
                                <Typography variant="body2">PostgreSQL Database</Typography>
                            </ServiceNode>
                        </Box>
                    </Grid>
                </Grid>



                {/* 3. KEY COMMANDS */}
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                    Compose <span style={{ color: DOCKER_BLUE }}>Workflow</span>
                </Typography>

                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            cmd: "docker-compose up -d",
                            icon: <RocketLaunchIcon />,
                            desc: "Builds, creates, and starts all services in detached mode."
                        },
                        {
                            cmd: "docker-compose down",
                            icon: <TerminalIcon />,
                            desc: "Stops and removes containers, networks, and images defined in the file."
                        },
                        {
                            cmd: "docker-compose ps",
                            icon: <FactCheckIcon />,
                            desc: "Lists the status of all containers managed by the current Compose file."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <Box sx={{ color: DOCKER_BLUE }}>{item.icon}</Box>
                                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', color: "#ADDB67" }}>{item.cmd}</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ color: BRAND.muted, fontSize: '0.85rem' }}>
                                    {item.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. THE PRO ADVANTAGE */}
                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(13, 183, 237, 0.05)",
                    borderLeft: `5px solid ${DOCKER_BLUE}`,
                    borderRadius: "0 16px 16px 0"
                }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                        💡 Why use Compose?
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                <strong>Reproducibility:</strong> Every developer on your team runs the exact same stack with the same configuration.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                <strong>Isolation:</strong> Each project gets its own network automatically, preventing port conflicts between different apps.
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Orchestration complete. Let's head to the Registry.</Typography>
                    <Link to="/docker-hub" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: Docker Hub & Registries
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
